import { useState, useContext } from "react";
import { deleteWish, updateWishStatus, updateWish } from "../../api";
import { AuthContext, YearContext } from "../../context/context";
import Dropdown from "../Elements/Dropdown";
import { TbTrash, TbEdit, TbExternalLink } from "react-icons/tb";
import { HiOutlineShoppingBag } from "react-icons/hi";
import Popup from "../Elements/Popup";
import InputText from "../Inputs/InputText";
import InputFile from "../Inputs/InputFile";
import { BsPatchCheckFill } from "react-icons/bs";
import { useLanguage } from "../../context/LanguageContext";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import InputSelect from "../Inputs/InputSelect";
import InputCurrency from "../Inputs/InputCurrency";
import PropTypes from "prop-types";

const WishCard = ({
  wishlistItem,
  showTitle = true,
  showPrice = true,
  publicView = false,
  onToast,
}) => {
  const { userData, setUserData } = useContext(AuthContext);
  const { years } = useContext(YearContext);
  const { copy } = useLanguage();

  const [popupType, setPopupType] = useState(null);

  // Validation schema for editing wishes (moved inside component)
  const EditWishSchema = z.object({
    wish_title: z.string().min(1, copy.card_wishlist.errors.title_required).max(35, "Title must be 35 characters or less"),
    wish_img: z
      .any()
      .refine((files) => {
        return files !== null && files !== undefined;
      }, copy.card_wishlist.errors.img_required)
      .refine((files) => {
        if (typeof files === "string") return true;

        const file = files[0];

        return file?.size < 5 * 1024 * 1024;
      }, copy.card_wishlist.errors.img_too_large),
    wish_url: z.string().min(1, copy.card_wishlist.errors.url_required).max(2000, "URL is too long (max 2000 characters)").refine((val) => val.startsWith("http"), copy.card_wishlist.errors.url_invalid),
    wish_price: z.preprocess(
      (val) => (val === "" ? undefined : Number(val)),
      z
        .number({ required_error: copy.card_wishlist.errors.price_required })
        .min(0.01, { message: copy.card_wishlist.errors.price_min })
    ),
    wish_currency: z
      .string()
      .min(1, copy.card_wishlist.errors.currency_required),
    year: z.preprocess(
      (val) => (typeof val === "string" ? Number(val) : val),
      z.number({ required_error: copy.card_wishlist.errors.year_required })
    ),
  });

  // React Hook Form setup with Zod validation
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    watch,
  } = useForm({
    resolver: zodResolver(EditWishSchema),
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    defaultValues: {
      wish_title: wishlistItem.title,
      wish_img: wishlistItem.img,
      wish_url: wishlistItem.url,
      wish_price: wishlistItem.price,
      wish_currency: wishlistItem.currency,
      year: wishlistItem.year,
    },
  });

  const [editFormData, setEditFormData] = useState({
    img: wishlistItem.img,
  });

  const openPopup = (type) => setPopupType(type);
  const closePopup = () => {
    if (window.getSelection().toString() === "") {
      setPopupType(null);
    }
  };

  // Start delete process
  const handleDeleteClickWish = () => {
    openPopup("wish-delete");
  };

  // Confirm deletion and update user data
  const handleDeleteWish = async () => {
    try {
      await deleteWish(wishlistItem.id);
      const updatedList = userData.wishlists.filter(
        (wish) => wish.id !== wishlistItem.id
      );
      setUserData({ ...userData, wishlists: updatedList });
      onToast({ type: "success", message: copy.card_wishlist.toast.deleted });
      closePopup();
    } catch (error) {
      onToast({
        type: "error",
        message: error.message || copy.card_wishlist.toast.delete_failed,
      });
    }
  };

  // Toggle status of a wish (e.g., mark as purchased)
  const toggleWishStatus = async () => {
    try {
      const updatedStatus = !wishlistItem.status;

      const updatedWishlist = userData.wishlists.map((wish) =>
        wish.id === wishlistItem.id ? { ...wish, status: updatedStatus } : wish
      );

      if (updatedStatus) {
        updatedWishlist.sort((a) => (a.status ? 1 : -1));
      }

      setUserData({ ...userData, wishlists: updatedWishlist });

      const result = await updateWishStatus(wishlistItem.id, updatedStatus);
      if (result) {
        onToast({
          type: "success",
          message: updatedStatus
            ? copy.card_wishlist.toast.marked_completed
            : copy.card_wishlist.toast.marked_incomplete,
        });
      } else {
        onToast({
          type: "error",
          message: copy.card_wishlist.toast.status_failed,
        });
      }
    } catch (error) {
      console.error(error);
      onToast({
        type: "error",
        message: copy.card_wishlist.toast.status_error,
      });
    }
  };

  // Pre-fill and open the edit popup
  const handleEditClickWish = () => {
    setEditFormData({ img: wishlistItem.img });
    openPopup("wish-edit");
  };

  // Submit updated wish data
  const handleEditWish = handleSubmit(
    async (data) => {
      const imageFile =
        typeof editFormData.img === "object" ? editFormData.img : null;

      const updatedWishData = {
        title: data.wish_title,
        url: data.wish_url,
        price: data.wish_price,
        currency: data.wish_currency,
        year: data.year,
        img: typeof editFormData.img === "string" ? editFormData.img : "",
      };

      try {
        const result = await updateWish(
          wishlistItem.id,
          updatedWishData,
          imageFile
        );

        if (result) {
          const updatedWishlist = userData.wishlists.map((wish) =>
            wish.id === wishlistItem.id
              ? { ...wish, ...updatedWishData, img: result.data.img }
              : wish
          );

          setUserData({ ...userData, wishlists: updatedWishlist });
          onToast({
            type: "success",
            message: copy.card_wishlist.toast.updated,
          });
          closePopup();
        }
      } catch (error) {
        onToast({
          type: "error",
          message: error.message || copy.card_wishlist.toast.update_failed,
        });
      }
    },
    (validationErrors) => {
      // Validation errors are already displayed inline, no need for toast
      console.error("Validation errors:", validationErrors);
    }
  );

  // Only show dropdown/actions if not public view
  const wishDropdown = (wishId) =>
    [
      {
        text: copy.dropdown.link_shop,
        link: wishlistItem.url,
        icon: <TbExternalLink />,
        external: true,
      },
      !wishlistItem.status && {
        text: copy.dropdown.bought,
        onClick: toggleWishStatus,
        icon: <HiOutlineShoppingBag />,
      },
      {
        text: copy.dropdown.edit,
        onClick: () => handleEditClickWish(wishId),
        icon: <TbEdit />,
      },
      {
        text: copy.dropdown.delete,
        onClick: () => handleDeleteClickWish(),
        icon: <TbTrash />,
      },
    ].filter(Boolean);

  const publicDropdown = () => [
    {
      text: copy.dropdown.link_shop,
      link: wishlistItem.url,
      icon: <TbExternalLink />,
      external: true,
    },
  ];

  const allCurrencies = userData?.currencies || [
    "EUR",
    "USD",
    "PLN",
    "DKK",
    "GBP",
    "CAD",
    "AUD",
    "CHF",
    "JPY",
    "SEK",
    "NOK",
  ];

  return (
    <>
      <div className="w-full relative flex flex-col gap-2 transition-all duration-300">
        {/* Only show Dropdown if not publicView */}

        <div className="absolute top-2 right-2 z-10">
          {!publicView ? (
            <Dropdown
              options={wishDropdown(wishlistItem.id)}
              iconComponent="circle"
            />
          ) : (
            <Dropdown options={publicDropdown()} iconComponent="circle" />
          )}
        </div>

        <div className="relative w-full h-[165px] card-base-border rounded-md overflow-hidden">
          <img
            src={wishlistItem.img}
            alt={`Wish for ${wishlistItem.year}`}
            className="w-full h-full object-cover"
            height={163}
          />
          <div className="flex items-center gap-2 absolute bottom-2 left-2">
            <BsPatchCheckFill
              size={30}
              className={`text-accent-dark ${
                wishlistItem.status
                  ? "bg-white p-0.5 rounded-full drop-shadow-md"
                  : "hidden"
              }`}
            />
          </div>
        </div>
        {showTitle && (
          <div className="flex flex-col gap-0.5">
            <h3 className="md:text-sm text-base">{wishlistItem.title}</h3>
            {showPrice && (
              <p>
                {wishlistItem.price} {wishlistItem.currency}
              </p>
            )}
          </div>
        )}
      </div>

      <Popup
        isOpen={popupType === "wish-delete"}
        onClose={closePopup}
        title={copy.card_wishlist.popup.delete_title}
        description={copy.card_wishlist.popup.delete_description}
        buttonSecondaryCTA={copy.dropdown.cancel}
        onClickSecondaryLink={closePopup}
        buttonPrimaryCTA={copy.dropdown.delete}
        onClickPrimaryLink={handleDeleteWish}
      />

      <Popup
        isOpen={popupType === "wish-edit"}
        onClose={closePopup}
        title={copy.card_wishlist.popup.edit_title}
        buttonSecondaryCTA={copy.dropdown.cancel}
        onClickSecondaryLink={closePopup}
        buttonPrimaryCTA={copy.dropdown.save}
        onClickPrimaryLink={handleEditWish}
      >
        <form
          onSubmit={handleSubmit(handleEditWish)}
          className="flex flex-col gap-4"
        >
          <InputText
            label={copy.card_wishlist.form.label_title}
            {...register("wish_title")}
            error={errors.wish_title?.message}
            details={`${watch("wish_title")?.length || 0}/35 characters`}
            maxLength={35}
          />

          <InputFile
            label={copy.card_wishlist.form.label_img}
            {...register("wish_img")}
            onChange={(e) => {
              const file = e.target.files?.[0];
              setEditFormData({ ...editFormData, img: file });
              setValue("wish_img", e.target.files, { shouldValidate: true });
            }}
            error={errors.wish_img?.message}
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 w-full">
            <InputText
              label={copy.card_wishlist.form.label_link}
              placeholder={copy.card_wishlist.form.placeholder_link}
              {...register("wish_url")}
              error={errors.wish_url?.message}
            />
            <div className="w-full">
              <InputSelect
                label="Year"
                value={String(wishlistItem.year)}
                onChange={(e) => setValue("year", Number(e.target.value))}
                options={years.map((year) => ({
                  value: String(year),
                  label: String(year),
                }))}
                error={errors.year?.message}
                className="w-full"
              />
            </div>
            <InputText
              label={copy.card_wishlist.form.label_price}
              placeholder={copy.card_wishlist.form.placeholder_price}
              {...register("wish_price")}
              error={errors.wish_price?.message}
            />
            <InputCurrency
              label={copy.card_wishlist.form.label_currency}
              placeholder={copy.card_wishlist.form.placeholder_currency}
              error={errors.wish_currency?.message}
              setCurrency={(currency) => setValue("wish_currency", currency)}
              currencies={allCurrencies}
              {...register("wish_currency")}
            />
          </div>
        </form>
      </Popup>
    </>
  );
};

WishCard.propTypes = {
  wishlistItem: PropTypes.object.isRequired,
  showTitle: PropTypes.bool,
  showPrice: PropTypes.bool,
  publicView: PropTypes.bool,
  onToast: PropTypes.func,
};

export default WishCard;
